/**
 * useSquareLive — live Square price + availability overlay for the curated
 * menu page (/FoodMenu).
 *
 * The curated menu (menuConfig/menu-data.json) owns presentation AND price:
 * whatever an admin publishes from "Edit Menu" is what customers see. Square
 * owns stock. This provider fetches the live Square menu (shared cache with the
 * /order screen) and exposes a name-keyed lookup so rows can show a "Sold out"
 * badge without anyone maintaining it by hand.
 *
 * `priceCents` is still exposed, but it is only a fallback for items the
 * curated menu has no price for. It used to override the published price, which
 * made the desktop row disagree with the mobile list and made price edits look
 * like they hadn't saved — see MenuItem.js.
 *
 * Matching is by normalized item name — items that don't match simply keep
 * their curated price.
 */
import React, { createContext, useContext, useMemo } from 'react';
import useSquareMenu from './useSquareMenu';

const SquareLiveContext = createContext(null);

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

export function SquareLiveProvider({ children }) {
  // Badges only — the edge-cached variant is plenty fresh and saves every
  // visitor two Firestore reads on the server.
  const { categories } = useSquareMenu({ shared: true });

  const map = useMemo(() => {
    const m = new Map();
    (categories || []).forEach(c => (c.items || []).forEach(it => {
      const inStock = (it.variations || []).filter(v => !v.soldOut);
      const priceCents = (inStock[0] || (it.variations || [])[0])?.priceCents ?? it.basePriceCents ?? null;
      m.set(norm(it.name), { soldOut: !!it.soldOut, priceCents, id: it.id });
    }));
    return m;
  }, [categories]);

  return <SquareLiveContext.Provider value={map}>{children}</SquareLiveContext.Provider>;
}

/** Live Square info for a curated item name; null when no match / no provider. */
export function useSquareLiveItem(name) {
  const map = useContext(SquareLiveContext);
  if (!map || !name) return null;
  return map.get(norm(name)) || null;
}
